import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useRoleTabs } from '../../context/PermissionContext';
import VisitActionsPanel from '../visit/VisitActionsPanel';
import useWorkflowStore, { WORKFLOW_ACTIONS } from '../../stores/workflowStore';
import { formatToken, isAppointmentVisit } from '../../utils/formatToken';
import { PhoneButton } from '../common/PhoneButton';
import HandoffBadge from '../procedures/HandoffBadge';
import NurseTaskWidget from '../procedures/widget/NurseTaskWidget';
import {
  patientName,
  orderTabsByPatterns,
  CancelConfirmDialog, EmptyState,
} from './WorkflowShared';

// NurseView — Nurse vitals/details workflow
export default function NurseView({ activeVisits, uniqueDoctors, doctors, isDark, onNavigate, updateVisit, role, skipVisit, memberId }) {
  const [doctorFilter, setDoctorFilter] = useState('');
  const [patientSearch, setPatientSearch] = useState('');
  const [expandedVisit, setExpandedVisit] = useState(null);  // visit_id
  const [activeTab, setActiveTab] = useState(null);          // 'vitals' | 'symptoms' | 'notes' | 'report'
  const [cancelConfirm, setCancelConfirm] = useState(null);  // { visitId, name }

  const { logAction: logWorkflow, patterns, fetchPatterns } = useWorkflowStore();

  useEffect(() => {
    if (!patterns) fetchPatterns();
  }, [patterns, fetchPatterns]);

  const filtered = useMemo(() => {
    let list = activeVisits;
    if (doctorFilter) {
      list = list.filter(
        (v) => (v.doctor_member_id || v.visit_doctor_id) === doctorFilter
      );
    }
    if (patientSearch.trim()) {
      const q = patientSearch.toLowerCase().trim();
      list = list.filter((v) => patientName(v).toLowerCase().includes(q));
    }
    return list;
  }, [activeVisits, doctorFilter, patientSearch]);

  // Nurse tabs — derived from central permissions (no history for nurse)
  const NURSE_TABS = useRoleTabs();
  const orderedNurseTabs = useMemo(() => orderTabsByPatterns(NURSE_TABS, patterns), [NURSE_TABS, patterns]);

  // Cancel visit handler
  const handleCancelVisit = useCallback((visitId) => {
    const visit = (activeVisits || []).find((v) => v.visit_id === visitId);
    const name = visit ? patientName(visit) : 'this patient';
    setCancelConfirm({ visitId, name });
  }, [activeVisits]);

  const confirmCancelVisit = useCallback(async (comment) => {
    if (!cancelConfirm) return;
    const { visitId } = cancelConfirm;
    setCancelConfirm(null);
    setActiveTab(null);
    await updateVisit(visitId, { status: 'CANCELLED', cancellation_reason: comment, cancelled_by_role: role });
    logWorkflow(visitId, WORKFLOW_ACTIONS.MARK_CANCELLED);
  }, [cancelConfirm, updateVisit, logWorkflow, role]);

  const handleToggleCard = useCallback((visitId) => {
    setExpandedVisit((prev) => {
      if (prev === visitId) { setActiveTab(null); return null; }
      setActiveTab(null);
      return visitId;
    });
  }, []);

  const handleTabToggle = useCallback((key) => {
    setActiveTab((prev) => (prev === key ? null : key));
  }, []);

  const filterInputStyle = {
    padding: '0.375rem 0.625rem',
    borderRadius: '0.5rem',
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
    background: isDark ? 'rgba(255,255,255,0.05)' : '#fff',
    color: isDark ? '#f5f5f7' : '#111827',
    fontSize: '0.75rem',
    outline: 'none',
    width: '100%',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      {/* Phase 4.0 — assigned-tasks pill on the right edge */}
      <NurseTaskWidget viewer={{ memberId, role }} />

      {/* Filters */}
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 140px', minWidth: '120px' }}>
          <select
            value={doctorFilter}
            onChange={(e) => setDoctorFilter(e.target.value)}
            style={{ ...filterInputStyle, cursor: 'pointer' }}
          >
            <option value="">All Doctors</option>
            {uniqueDoctors.map((d) => {
              const docProfile = (doctors || []).find((dp) => (dp.member_id || dp.doctor_id) === d.id);
              const avail = docProfile?.availability || 'available';
              const suffix = avail === 'on_hold' ? ' ⏸ On Hold' : avail === 'closed_today' ? ' 🚫 Closed' : '';
              return (
                <option key={d.id} value={d.id}>
                  Dr. {d.name}{suffix}
                </option>
              );
            })}
          </select>
        </div>
        <div style={{ flex: '1 1 160px', minWidth: '120px' }}>
          <input
            type="text"
            placeholder="Search patient…"
            value={patientSearch}
            onChange={(e) => setPatientSearch(e.target.value)}
            style={filterInputStyle}
          />
        </div>
      </div>

      {/* Patient cards */}
      {filtered.length === 0 ? (
        <EmptyState isDark={isDark} icon="✅" title="No pending visits" subtitle="No patients to update right now" />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {filtered.slice(0, 8).map((visit) => {
            const isExpanded = expandedVisit === visit.visit_id;
            return (
              <div
                key={visit.visit_id}
                style={{
                  borderRadius: '0.625rem',
                  border: `1px solid ${isExpanded
                    ? isDark ? 'rgba(59,130,246,0.3)' : 'rgba(59,130,246,0.2)'
                    : isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
                  background: isDark ? 'rgba(255,255,255,0.03)' : '#fff',
                  overflow: 'hidden',
                  transition: 'border-color 0.15s ease',
                }}
              >
                {/* Card header — click to expand */}
                <button
                  onClick={() => handleToggleCard(visit.visit_id)}
                  style={{
                    width: '100%', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: '0.625rem',
                    padding: '0.625rem 0.75rem',
                    background: 'transparent', border: 'none', textAlign: 'left',
                  }}
                >
                  <div
                    style={{
                      width: '0.5rem', height: '0.5rem', borderRadius: '50%',
                      background: visit.status === 'IN_PROGRESS' ? '#8b5cf6' : '#3b82f6',
                      flexShrink: 0,
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                      <p style={{
                        margin: 0, fontSize: '0.8125rem', fontWeight: 600,
                        color: isDark ? '#f5f5f7' : '#111827',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {patientName(visit)}
                        {visit.queue_number ? (
                          <span style={{ fontWeight: 400, color: isDark ? '#6b7280' : '#9ca3af', marginLeft: '0.375rem' }}>
                            {formatToken(visit.queue_number, visit.appointment_number)}
                          </span>
                        ) : null}
                      </p>
                      <PhoneButton phoneNumber={visit.patient_mobile} isDark={isDark} size="sm" />
                      {isAppointmentVisit(visit) && <span style={{ fontSize: '0.5rem', padding: '0.0625rem 0.25rem', borderRadius: '0.1875rem', background: isDark ? 'rgba(168,85,247,0.15)' : 'rgba(168,85,247,0.1)', color: '#a855f7', fontWeight: 600, flexShrink: 0 }}>📅</span>}
                      <HandoffBadge visit={visit} viewerMemberId={memberId} isDark={isDark} />
                    </div>
                    <p style={{ margin: '0.125rem 0 0', fontSize: '0.6875rem', color: isDark ? '#9ca3af' : '#6b7280' }}>
                      {visit.doctor_name ? `Dr. ${visit.doctor_name}` : ''}
                      {visit.status === 'IN_PROGRESS' ? ' · In Progress' : ' · In Queue'}
                    </p>
                  </div>
                  <span style={{ fontSize: '0.625rem', color: isDark ? '#6b7280' : '#9ca3af', flexShrink: 0 }}>
                    {isExpanded ? '▲' : '▼'}
                  </span>
                </button>

                {/* Expanded content — inline actions */}
                {isExpanded && (
                  <div style={{ padding: '0 0.75rem 0.625rem', borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'}` }}>
                    {/* Tab bar */}
                    <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap', padding: '0.5rem 0 0.25rem' }}>
                      {orderedNurseTabs.map((tab) => {
                        const isActive = activeTab === tab.key;
                        return (
                          <button
                            key={tab.key}
                            onClick={() => handleTabToggle(tab.key)}
                            style={{
                              cursor: 'pointer',
                              display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                              padding: '0.375rem 0.625rem', borderRadius: '0.5rem',
                              fontSize: '0.6875rem', fontWeight: isActive ? 600 : 400,
                              border: isActive
                                ? `1.5px solid ${isDark ? '#3b82f6' : '#2563eb'}`
                                : `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
                              background: isActive
                                ? isDark ? 'rgba(59,130,246,0.15)' : 'rgba(37,99,235,0.08)'
                                : 'transparent',
                              color: isActive ? '#3b82f6' : isDark ? '#d4d4d8' : '#374151',
                              transition: 'background 0.15s, color 0.15s, border-color 0.15s',
                            }}
                          >
                            <span>{tab.icon}</span>
                            <span>{tab.label}</span>
                            {isActive && <span style={{ fontSize: '0.5rem', marginLeft: '0.1rem', opacity: 0.7 }}>▲</span>}
                          </button>
                        );
                      })}

                      {/* Cancel button — pinned right */}
                      {visit.status === 'QUEUE' && (
                        <button
                          onClick={() => handleCancelVisit(visit.visit_id)}
                          style={{
                            cursor: 'pointer',
                            display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                            padding: '0.375rem 0.625rem', borderRadius: '0.5rem',
                            fontSize: '0.6875rem', fontWeight: 500,
                            border: `1px solid ${isDark ? 'rgba(239,68,68,0.2)' : 'rgba(239,68,68,0.15)'}`,
                            background: isDark ? 'rgba(239,68,68,0.08)' : 'rgba(239,68,68,0.05)',
                            color: '#ef4444',
                            marginLeft: 'auto',
                            transition: 'background 0.15s, opacity 0.15s',
                          }}
                        >
                          <span>✕</span>
                          <span>Cancel</span>
                        </button>
                      )}

                      {/* Skip button — for QUEUE and IN_PROGRESS */}
                      {(visit.status === 'QUEUE' || visit.status === 'IN_PROGRESS') && (
                        <button
                          onClick={() => skipVisit?.(visit.visit_id, role)}
                          style={{
                            cursor: 'pointer',
                            display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                            padding: '0.375rem 0.625rem', borderRadius: '0.5rem',
                            fontSize: '0.6875rem', fontWeight: 500,
                            border: `1px solid ${isDark ? 'rgba(245,158,11,0.25)' : 'rgba(245,158,11,0.2)'}`,
                            background: isDark ? 'rgba(245,158,11,0.1)' : 'rgba(245,158,11,0.06)',
                            color: isDark ? '#f59e0b' : '#d97706',
                            transition: 'background 0.15s, opacity 0.15s',
                          }}
                        >
                          <span>⏭</span>
                          <span>Skip</span>
                        </button>
                      )}
                    </div>

                    {/* Panel */}
                    {activeTab && (
                      <VisitActionsPanel
                        visit={visit}
                        userRole="NURSE"
                        initialTab={activeTab}
                        hideActionBar
                        onClose={() => setActiveTab(null)}
                      />
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {filtered.length > 8 && (
            <button
              onClick={() => onNavigate?.(null)}
              style={{
                width: '100%', cursor: 'pointer',
                padding: '0.375rem',
                borderRadius: '0.5rem',
                border: `1px dashed ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
                background: 'transparent',
                fontSize: '0.625rem', color: isDark ? '#6b7280' : '#9ca3af',
                textAlign: 'center',
              }}
            >
              View all {filtered.length} visits →
            </button>
          )}
        </div>
      )}

      {/* Cancel Confirmation Dialog with mandatory comment */}
      {cancelConfirm && (
        <CancelConfirmDialog
          isDark={isDark}
          name={cancelConfirm.name}
          onConfirm={confirmCancelVisit}
          onCancel={() => setCancelConfirm(null)}
        />
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════
// ADMIN VIEW
// ═══════════════════════════════════════════════════════════════════════
